import {
  faIndent,
  faDollarSign,
  faFileCode,
  faPoll,
  faBars,
  faRectangleWide,
  faPenField,
  faListCheck,
  faClock,
  faBell,
  faSliders,
  faUpload,
  faTable,
  faCode,
  faChartLine
} from '@fortawesome/pro-solid-svg-icons'

import { ComponentTitle, ComponentTypes } from 'pages/interfaces/components/types'
import {
  Agent,
  AgentChatMessage,
  AgentChatRole,
  AgentCode,
  AgentQuestion,
  AgentStatus,
  ChatSession,
  ContextSettings,
  EnableOnCondition,
  GeneratedFile
} from 'store/agent'

export type AiWidgetPosition = 'left' | 'right'

/**
 * The chat is always docked to one side. Anything else persisted in the store or
 * in localStorage — most notably the 'floating' position that no longer exists —
 * resolves to the right dock.
 */
export const resolveAiWidgetPosition = (aiWidgetPos?: string | null): AiWidgetPosition =>
  (aiWidgetPos ?? localStorage.getItem('aiWidgetPos')) === 'left' ? 'left' : 'right'

export enum AgentContext {
  ADD_NODE = 'add_node',
  ADD_FILE = 'add_file',
  ADD_COMPONENT = 'add_component',
  ADD_INTERFACE = 'add_interface',
  CAPTURE_SCREEN = 'capture_screen',
  NONE = ''
}
export enum ContextType {
  NODE = 'node_list',
  COMPONENT = 'component_list',
  FILE = 'uploaded_files'
}

interface ChatExportMessage {
  role: string
  content?: string
}

interface BuildChatExportMarkdownOptions {
  title?: string
  assistantLabel?: string
}

const CHAT_ROLE_LABELS: Record<string, string> = {
  user: 'User',
  assistant: 'Assistant',
  reasoning: 'Reasoning',
  tool_call: 'Tool Call',
  tool: 'Tool',
  system: 'System'
}

const getChatRoleLabel = (role: string, assistantLabel?: string): string => {
  if (role === 'assistant' && assistantLabel) {
    return assistantLabel
  }

  if (CHAT_ROLE_LABELS[role]) {
    return CHAT_ROLE_LABELS[role]
  }

  return role
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const getFenceToken = (content: string): string => {
  const codeFenceMatches = content.match(/`+/g)
  const longestFence = codeFenceMatches?.reduce((max, current) => {
    return Math.max(max, current.length)
  }, 0)

  return '`'.repeat(Math.max(3, (longestFence || 0) + 1))
}

const formatPlainTextBlock = (content: string): string => {
  const normalized = content.replace(/\r\n/g, '\n').trimEnd()
  if (!normalized) {
    return '_No content._'
  }

  const fence = getFenceToken(normalized)
  return `${fence}text\n${normalized}\n${fence}`
}

const formatAssistantContent = (content: string): string => {
  const normalized = content.replace(/\r\n/g, '\n').trim()
  return normalized || '_No content._'
}

const formatUtcDateTime = (date: Date): string => {
  const year = date.getUTCFullYear()
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0')
  const day = `${date.getUTCDate()}`.padStart(2, '0')
  const hours = `${date.getUTCHours()}`.padStart(2, '0')
  const minutes = `${date.getUTCMinutes()}`.padStart(2, '0')
  const seconds = `${date.getUTCSeconds()}`.padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

export const formatQuestionForExport = ({ question, options }: AgentQuestion): string =>
  [
    question,
    ...options.map(({ label, description }) =>
      description ? `- ${label}: ${description}` : `- ${label}`
    )
  ].join('\n')

export const buildChatExportMarkdown = (
  messages: ChatExportMessage[],
  parseAssistantContent?: (messageContent: string) => string,
  options?: BuildChatExportMarkdownOptions
): string => {
  const exportDate = formatUtcDateTime(new Date())
  const exportTitle = options?.title?.trim() || 'AssistantBot Chat Export'
  const assistantLabel = options?.assistantLabel?.trim()

  const sections = messages.map((message, index) => {
    const role = message.role || 'system'
    const roleLabel = getChatRoleLabel(role, assistantLabel)
    const rawContent = message.content || ''

    const content =
      role === 'assistant'
        ? formatAssistantContent(
            parseAssistantContent ? parseAssistantContent(rawContent) : rawContent
          )
        : formatPlainTextBlock(rawContent)

    return `## ${index + 1}. ${roleLabel}\n\n${content}`
  })

  if (sections.length === 0) {
    sections.push('## 1. System\n\n_No messages available._')
  }

  return `# ${exportTitle}\n\nExported at: ${exportDate} (UTC)\n\n${sections.join('\n\n---\n\n')}\n`
}

// Escape bare `$` signs so KaTeX renders them literally (e.g. currency such as
// `US$`) instead of failing to parse. Already-escaped `\$` are left untouched.
const escapeMathDollars = (body: string): string => body.replace(/(?<!\\)\$/g, '\\$')

/**
 * Convert the LaTeX math delimiters that assistant/LLM responses commonly emit
 * (`\[ … \]` for display math and `\( … \)` for inline math) into the
 * `$$ … $$` / `$ … $` delimiters understood by remark-math, so KaTeX can
 * typeset the formulas. Content inside fenced or inline code spans is left
 * verbatim so Python snippets are never rewritten as math.
 */
export const normalizeMathDelimiters = (content: string): string => {
  if (!content) return content

  // Split on fenced (```…```) and inline (`…`) code spans so `\[`, `\(` or
  // stray `$` inside snippets are never interpreted as math delimiters. The
  // captured code spans land on the odd indexes of the resulting array.
  const segments = content.split(/(```[\s\S]*?```|`[^`\n]*`)/g)

  return segments
    .map((segment, index) => {
      if (index % 2 === 1) return segment

      return (
        segment
          // Display math: \[ … \] → $$ … $$
          .replace(/\\\[([\s\S]+?)\\\]/g, (_match, body) => `$$${escapeMathDollars(body)}$$`)
          // Inline math: \( … \) → $ … $ (trim: remark-math ignores inline
          // math that has whitespace right after the opening delimiter).
          .replace(/\\\(([\s\S]+?)\\\)/g, (_match, body) => `$${body.trim()}$`)
      )
    })
    .join('')
}

export const formatCode = (text: string) => {
  const lines = text.split('\n')
  let inCodeBlock = false
  let formattedText = ''

  lines.forEach((line) => {
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock
      if (inCodeBlock) {
        formattedText += '<pre><code>'
      } else {
        formattedText += '</code></pre>'
      }
    } else {
      formattedText += line + '\n'
    }
  })

  return formattedText
}
export const buildDocxContent = (chatContent: string): string => {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>AssistantBot Export</title>
        <style>
          pre {
            background-color: #f4f4f4;
            padding: 10px;
            overflow-x: auto;
          }
          code {
            font-family: 'Courier New', Courier, monospace;
            font-size: 14px;
          }
        </style>
      </head>
      <body>
        <h1>AssistantBot Export</h1>
        <div>${chatContent}</div>
      </body>
    </html>`
}
export const getComponentIcon = (componentType: ComponentTypes) => {
  switch (componentType) {
    case ComponentTypes.Core_Index:
      return faIndent
    case ComponentTypes.Core_InputData:
      return faPenField
    case ComponentTypes.Core_Indicator:
      return faDollarSign
    case ComponentTypes.Core_Html:
      return faFileCode
    case ComponentTypes.Core_DynamicHtml:
      return faCode
    case ComponentTypes.Core_Button:
      return faRectangleWide
    case ComponentTypes.Core_Menu:
      return faBars
    case ComponentTypes.Core_MyTasks:
      return faListCheck
    case ComponentTypes.Core_Notifications:
      return faBell
    case ComponentTypes.Core_ScheduledTasks:
      return faClock
    case ComponentTypes.Core_Filter:
      return faSliders
    case ComponentTypes.Core_UploadManager:
      return faUpload
    case ComponentTypes.Core_Table:
      return faTable
    case ComponentTypes.Core_HtmlVisualization:
    case ComponentTypes.Core_Chart:
      return faChartLine
    default:
      return faPoll
  }
}
export interface ComponentContext {
  component_id: string
  title: string
  component_type: ComponentTypes
  interface_id: string
  pinned?: boolean
}
export interface FileContext {
  name: string
  path: string
}
export const getComponentTitleForContext = (
  componentType: ComponentTypes,
  componentTitle: ComponentTitle
) => {
  if (componentTitle.custom_text) {
    return componentTitle.custom_text
  }
  if (componentTitle.text) {
    return componentTitle.text
  }
  return `[${componentType.split('core.')[1]}]` || componentType
}
const isAgentDisabledInSection = (agent?: Agent, currentSection?: string) => {
  const sections = agent?.enable_on_sections
  if (Array.isArray(sections) && sections.length > 0) {
    if (sections[0] === '*') {
      return false
    }
    return !sections.includes(currentSection || '')
  }
  return false
}
const isAgentDisabledOnConditions = (
  agent?: Agent,
  section?: string,
  editMode?: boolean,
  nodeIsCalc?: boolean
) => {
  if (Array.isArray(agent?.enable_on_conditions)) {
    if (
      agent!.enable_on_conditions.includes(EnableOnCondition.INTERFACES_EDIT_MODE) &&
      section === '/interfaces' &&
      !editMode
    ) {
      return true
    }
    if (
      agent!.enable_on_conditions.includes(EnableOnCondition.CODE_NODE_IS_CALC) &&
      section === '/code' &&
      !nodeIsCalc
    ) {
      return true
    }
  }
  return false
}
export const isAgentDisabled = (agent?: Agent, editMode?: boolean, nodeIsCalc?: boolean) => {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const rootPath = '/' + (pathname.split('/').filter(Boolean)[0] || '')
  // Check enable_on_sections
  const agentDisabledInSection = isAgentDisabledInSection(agent, rootPath)
  if (agentDisabledInSection) {
    return true
  }
  // Check enable_on_conditions
  return isAgentDisabledOnConditions(agent, rootPath, editMode, nodeIsCalc)
}

export const selectDefaultAgent = (
  agents: Agent[],
  selectedAgent?: Agent,
  pathname?: string
): Agent | undefined => {
  if (selectedAgent?.code) {
    // Keep the current agent selected if there is one selected
    return selectedAgent
  }

  // A single unified Pyplan agent serves every section now.
  return agents.find((a) => a.code === AgentCode.PYPLAN) ?? agents[0]
}
/**
 * Check if component context is required based on agent settings
 */
export const shouldRequireComponent = (
  currentSession?: ChatSession,
  contextSettings?: ContextSettings
) => {
  if (!contextSettings?.enable_components) return false
  const min = contextSettings?.min_components ?? 0
  const len = currentSession?.context?.component_list?.length ?? 0
  return min > 0 && len < min
}

export const enableSendButton = (selectedAgent?: Agent, currentSession?: ChatSession) => {
  if (selectedAgent?.context_settings?.enable_components) {
    const componentList = currentSession?.context?.component_list || []
    const minComponents = selectedAgent.context_settings.min_components || 0
    const maxComponents = selectedAgent.context_settings.max_components || Infinity
    if (componentList.length < minComponents || componentList.length > maxComponents) {
      return false
    }
  }
  if (selectedAgent?.context_settings?.enable_nodes) {
    const nodeList = currentSession?.context?.node_list || []
    const minNodes = selectedAgent.context_settings.min_nodes || 0
    const maxNodes = selectedAgent.context_settings.max_nodes || Infinity
    if (nodeList.length < minNodes || nodeList.length > maxNodes) {
      return false
    }
  }
  return true
}
export const getChatSessionCode = (agentCode: AgentCode, context?: Record<string, any>): string => {
  return agentCode
}

interface ChatMessageStyleMetadata {
  message: AgentChatMessage
  index: number
  showSoftSeparator: boolean
  canRestore: boolean
  checkpointIndex?: number
}

const isAssistantFlowRole = (role?: AgentChatRole) => {
  return (
    role === AgentChatRole.assistant ||
    role === AgentChatRole.reasoning ||
    role === AgentChatRole.tool_call ||
    role === AgentChatRole.question
  )
}

export const buildChatMessageStyleMetadata = (
  messages: AgentChatMessage[] = [],
  currentSession?: ChatSession
): ChatMessageStyleMetadata[] => {
  let checkpointCursor = 0

  return messages.map((message, index, allMessages) => {
    const previousMessage = allMessages[index - 1]
    const showSoftSeparator =
      !!previousMessage?.role &&
      previousMessage.role === AgentChatRole.user &&
      isAssistantFlowRole(message.role)

    // Command messages (e.g. /compact) are shown in the chat but never
    // persisted in the server session, so they must not shift the
    // checkpoint indexes used by restore.
    const isCommandMessage =
      message.role === AgentChatRole.user && message.content?.trim().toLowerCase() === '/compact'
    const isRestorableMessage = message.role === AgentChatRole.user && !isCommandMessage
    const checkpointIndex = isRestorableMessage ? checkpointCursor++ : undefined
    const hasAssistantResponseAfter = allMessages
      .slice(index + 1)
      .some(
        (currentMessage) =>
          currentMessage.role === AgentChatRole.assistant ||
          currentMessage.role === AgentChatRole.question
      )
    const canRestore =
      currentSession?.status === AgentStatus.COMPLETED &&
      isRestorableMessage &&
      checkpointIndex !== undefined &&
      hasAssistantResponseAfter

    return {
      message,
      index,
      showSoftSeparator,
      canRestore,
      checkpointIndex
    }
  })
}

export const COMPACT_COMMAND = '/compact'

export const mapSessionMessages = (
  messages: {
    role: string
    content?: string
    generated_files?: GeneratedFile[]
    question?: AgentQuestion
  }[]
): AgentChatMessage[] =>
  messages.flatMap((message): AgentChatMessage[] => {
    if (message.role === 'assistant') {
      return [
        {
          role: AgentChatRole.assistant,
          content: message.content,
          ...(message.generated_files?.length && {
            generatedFiles: message.generated_files
          })
        }
      ]
    }

    if (message.role === 'user') {
      return [{ role: AgentChatRole.user, content: message.content }]
    }

    // Reasoning / tool-call steps: kept so a reopened session shows the agent's
    // work (grouped into the activity block), same as during a live turn.
    if (message.role === 'reasoning') {
      return [{ role: AgentChatRole.reasoning, content: message.content }]
    }

    if (message.role === 'tool_call') {
      return [{ role: AgentChatRole.tool_call, content: message.content }]
    }

    if (message.role === 'question' && message.question) {
      return [{ role: AgentChatRole.question, question: message.question }]
    }

    return []
  })
